/**
 * Tela principal de bipagem do Terminal Kiosk — Produção v2 (P3).
 *
 * Corte único (decisão 4): mesmo shell/rota do kiosk, conteúdo passa a
 * operar o fluxo PEÇA × POSTO com os 3 carimbos do D2:
 *
 *   1. Operador digita/escaneia OP-UN1-NNNN (nº da peça)
 *   2. Carrega contexto (peça + ambiente + lote + próxima etapa bipável +
 *      compatibilidade com o posto da sessão + ação sugerida)
 *   3. Botão GRANDE da ação sugerida (ENTRAR NA FILA -> INICIAR ->
 *      CONCLUIR) + PAUSAR/RETOMAR secundários + toggle "setup" discreto
 *   4. CONCLUIR (run): mini-form qtd boa (default 1) / qtd refugo
 *      (default 0); posto de CONFERÊNCIA registra não-conformidades
 *      tipadas ({tipo 6M, descricao}) — D12
 *   5. Fora de ordem (422): aviso + FORÇAR para supervisor (motivo
 *      obrigatório — auditado), como no fluxo v1
 *   6. Som ok/erro/aviso + histórico recente da sessão com hh:mm:ss
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  Alert,
  ButtonRow,
  DangerButton,
  FieldLabel,
  FieldRow,
  HistoricoRecente,
  Input,
  KioskBody,
  KioskCard,
  OpContextoBox,
  Pill,
  PrimaryButton,
  SecondaryButton,
  Spacer,
  SuccessButton,
  Title,
} from "./styles";
import {
  biparPeca,
  contextoPeca,
  lerEhSupervisor,
  lerOperadorNome,
  lerPosto,
  logout as fazerLogout,
} from "./client";
import { useSomFeedback } from "./useSomFeedback";
import { rotuloCheckpoint } from "../../services/producaoV2";

// Rótulos das ações do D2 (3 carimbos + pausa/retomada).
const ACAO_LABELS = {
  ENTROU_FILA: "ENTRAR NA FILA",
  INICIAR: "INICIAR",
  CONCLUIR: "CONCLUIR",
  PAUSAR: "PAUSAR",
  RETOMAR: "RETOMAR",
};

const ACAO_FEITA_LABELS = {
  ENTROU_FILA: "entrou na fila",
  INICIAR: "iniciada",
  CONCLUIR: "concluída",
  PAUSAR: "pausada",
  RETOMAR: "retomada",
};

// Tipos de não-conformidade da Conferência (D12) — categorias 6M.
const TIPOS_NC = [
  { codigo: "MAQUINA", label: "Máquina" },
  { codigo: "MAO_DE_OBRA", label: "Mão de obra" },
  { codigo: "MATERIAL", label: "Material" },
  { codigo: "METODO", label: "Método" },
  { codigo: "MEDICAO", label: "Medição" },
  { codigo: "MEIO_EXTERNO", label: "Meio externo" },
];

function formatarHora(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString("pt-BR", {
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
  } catch {
    return "";
  }
}

export default function TerminalBipagem() {
  const navigate = useNavigate();
  const { tocarOk, tocarErro, tocarAviso } = useSomFeedback();
  const inputRef = useRef(null);

  const [numero, setNumero] = useState("");
  const [carregandoContexto, setCarregandoContexto] = useState(false);
  const [contexto, setContexto] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");
  const [historico, setHistorico] = useState([]);

  // D2 — setup separado do run (toggle discreto).
  const [ehSetup, setEhSetup] = useState(false);
  // Mini-form do CONCLUIR (run).
  const [qtdBoa, setQtdBoa] = useState("1");
  const [qtdRefugo, setQtdRefugo] = useState("0");
  const [ncs, setNcs] = useState([]); // [{tipo, descricao}] — Conferência

  const posto = lerPosto();
  const operadorNome = lerOperadorNome();
  const ehSupervisor = lerEhSupervisor();
  const postoConferencia = posto?.etapa_codigo === "CONFERENCIA";

  const focar = useCallback(() => {
    inputRef.current?.focus?.();
  }, []);

  useEffect(() => {
    focar();
  }, [focar]);

  useEffect(() => {
    if (!sucesso) return undefined;
    const t = setTimeout(() => setSucesso(""), 4000);
    return () => clearTimeout(t);
  }, [sucesso]);

  function limparParaProxima() {
    setNumero("");
    setContexto(null);
    setEhSetup(false);
    setQtdBoa("1");
    setQtdRefugo("0");
    setNcs([]);
    focar();
  }

  async function carregarContexto(numeroRaw) {
    const n = (numeroRaw || "").trim();
    if (!n) return;
    setErro("");
    setSucesso("");
    setContexto(null);
    setEhSetup(false);
    setQtdBoa("1");
    setQtdRefugo("0");
    setNcs([]);
    setCarregandoContexto(true);
    try {
      const ctx = await contextoPeca(n);
      setContexto(ctx);
      if (!ctx.compatible) tocarAviso();
    } catch (err) {
      tocarErro();
      if (err.status === 401 || err.status === 403) {
        navigate("/producao/terminal");
        return;
      }
      setErro(err.message || "Peça não encontrada.");
    } finally {
      setCarregandoContexto(false);
    }
  }

  function handleInputKey(e) {
    if (e.key === "Enter") {
      e.preventDefault();
      carregarContexto(numero);
    }
  }

  function handleInputBlur() {
    setTimeout(focar, 50);
  }

  async function executarBipagem(acao, {
    override = false, motivoOverride = "",
  } = {}) {
    const peca = contexto?.peca;
    if (!peca?.numero) return;
    setErro("");
    setEnviando(true);
    try {
      const ehConclusaoRun = acao === "CONCLUIR" && !ehSetup;
      const resp = await biparPeca({
        peca_numero: peca.numero,
        acao,
        eh_setup: ehSetup && (acao === "INICIAR" || acao === "CONCLUIR"),
        qtd_boa: ehConclusaoRun ? Number(qtdBoa || 0) : undefined,
        qtd_refugo: ehConclusaoRun ? Number(qtdRefugo || 0) : undefined,
        nao_conformidades:
          ehConclusaoRun && postoConferencia
            ? ncs.filter((nc) => nc.tipo)
            : undefined,
        override_supervisor: override,
        motivo_override: motivoOverride,
      });
      tocarOk();
      setSucesso(
        `Peça ${resp.peca.numero} ${ACAO_FEITA_LABELS[acao] || acao} `
        + `em ${posto?.nome || "posto"}`
        + `${ehSetup && (acao === "INICIAR" || acao === "CONCLUIR") ? " (setup)" : ""}`
        + `${override ? " (override supervisor)" : ""}.`,
      );
      setHistorico((prev) => [
        {
          id: resp.evento.id,
          peca_numero: resp.peca.numero,
          acao,
          eh_setup: Boolean(resp.evento.eh_setup),
          timestamp: resp.evento.timestamp,
          situacao: resp.peca.situacao,
          override,
        },
        ...prev.slice(0, 9),
      ]);
      limparParaProxima();
    } catch (err) {
      tocarErro();
      if (err.status === 401) {
        navigate("/producao/terminal");
        return;
      }
      const codigo = err?.payload?.codigo;
      if (err.status === 403 && codigo !== "override_requer_supervisor") {
        navigate("/producao/terminal");
        return;
      }
      // 422 fora de ordem com supervisor: oferece FORÇAR no próprio erro.
      if (err.status === 422 && ehSupervisor && !override) {
        const esperada = rotuloCheckpoint(err?.payload?.etapa_esperada);
        setErro(
          `${err.message} Como supervisor, use FORÇAR para bipar mesmo `
          + `assim (checkpoint esperado: ${esperada}).`,
        );
        return;
      }
      setErro(err.message || `Falha ao ${ACAO_LABELS[acao] || acao}.`);
    } finally {
      setEnviando(false);
    }
  }

  // Override do supervisor (fora de ordem) — motivo obrigatório, auditado.
  async function handleForcar(acao) {
    if (!ehSupervisor) return;
    const checkpointEsperado = rotuloCheckpoint(
      contexto?.proxima_etapa_bipavel,
    );
    const motivo = window.prompt(
      `OVERRIDE — você vai bipar ${ACAO_LABELS[acao] || acao} no posto `
      + `${posto?.nome || ""} pulando o checkpoint esperado `
      + `(${checkpointEsperado}). Informe o motivo (será auditado):`,
      "",
    );
    if (!motivo || !motivo.trim()) {
      setErro("Override cancelado: motivo obrigatório.");
      return;
    }
    await executarBipagem(acao, {
      override: true, motivoOverride: motivo.trim(),
    });
  }

  async function handleLogout() {
    await fazerLogout();
    navigate("/producao/terminal");
  }

  function alterarNc(idx, campo, valor) {
    setNcs((prev) =>
      prev.map((nc, i) => (i === idx ? { ...nc, [campo]: valor } : nc)),
    );
  }

  const peca = contexto?.peca;
  const acaoSugerida = contexto?.acao_sugerida;
  const conclusaoRun = acaoSugerida === "CONCLUIR" && !ehSetup;

  return (
    <KioskBody>
      <KioskCard>
        <Title>Bipar peça</Title>
        <p style={{ margin: "-16px 0 24px", textAlign: "center", color: "#a4adba" }}>
          Posto:{" "}
          <strong style={{ color: "#d97706" }}>
            {posto?.nome || "—"}
          </strong>
          {posto?.etapa_label ? ` · ${posto.etapa_label}` : ""}
          {operadorNome ? ` · ${operadorNome}` : ""}
        </p>

        <FieldRow>
          <FieldLabel>Número da peça</FieldLabel>
          <Input
            ref={inputRef}
            value={numero}
            placeholder="OP-UN1-NNNN"
            onChange={(e) => setNumero(e.target.value.toUpperCase())}
            onKeyDown={handleInputKey}
            onBlur={handleInputBlur}
            disabled={carregandoContexto || enviando}
            autoFocus
          />
        </FieldRow>

        {erro && <Alert $tone="danger">{erro}</Alert>}
        {sucesso && <Alert $tone="success">✓ {sucesso}</Alert>}

        {contexto && peca && (
          <OpContextoBox>
            <div className="numero">{peca.numero}</div>
            <div className="desc">{peca.descricao}</div>
            <div className="meta">
              <span>
                Ambiente:{" "}
                <strong>
                  {contexto.ambiente
                    ? `${contexto.ambiente.numero} · ${contexto.ambiente.nome}`
                    : "—"}
                </strong>
              </span>
              <span>
                Lote: <strong>{contexto.lote_numero || "—"}</strong>
              </span>
              <span>
                Pedido: <strong>{peca.pedido_numero || "—"}</strong>
              </span>
              <span>
                Situação:{" "}
                <Pill $tone={peca.situacao === "CONCLUIDA" ? "success" : "info"}>
                  {peca.situacao}
                </Pill>
              </span>
              <span>
                Próximo checkpoint:{" "}
                <strong>
                  {rotuloCheckpoint(contexto.proxima_etapa_bipavel)}
                </strong>
              </span>
            </div>

            {contexto.ambiente?.congelado && (
              <Alert $tone="warning" style={{ marginTop: 12, marginBottom: 0 }}>
                Ambiente {contexto.ambiente.numero} está CONGELADO — a
                bipagem vale, mas confirme com o PCP antes de seguir.
              </Alert>
            )}

            {!contexto.compatible && (
              <>
                <Alert $tone="warning" style={{ marginTop: 16, marginBottom: 0 }}>
                  {contexto.mensagem_diagnostico
                    || `Esta peça ainda não chegou neste checkpoint — o próximo é ${rotuloCheckpoint(contexto.proxima_etapa_bipavel)}.`}
                </Alert>
                {ehSupervisor && contexto.fluxo_v2 && (
                  <>
                    <Spacer />
                    <ButtonRow>
                      <DangerButton
                        onClick={() => handleForcar("INICIAR")}
                        disabled={enviando}
                        title="Override do supervisor — bipar fora de ordem com motivo (auditado)"
                      >
                        {enviando ? "..." : "⚠ FORÇAR INICIAR"}
                      </DangerButton>
                      <DangerButton
                        onClick={() => handleForcar("CONCLUIR")}
                        disabled={enviando}
                        title="Override do supervisor — concluir fora de ordem com motivo (auditado)"
                      >
                        {enviando ? "..." : "⚠ FORÇAR CONCLUIR"}
                      </DangerButton>
                    </ButtonRow>
                    <Spacer $h={8} />
                  </>
                )}
                <Spacer $h={8} />
                <SecondaryButton
                  onClick={limparParaProxima}
                  disabled={enviando}
                >
                  Cancelar
                </SecondaryButton>
              </>
            )}

            {contexto.compatible && (
              <>
                <Spacer />

                {/* Toggle discreto de SETUP (D2 — setup separado do run). */}
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    fontSize: 14,
                    color: ehSetup ? "#fbbf24" : "#a4adba",
                    marginBottom: 12,
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={ehSetup}
                    onChange={(e) => setEhSetup(e.target.checked)}
                    disabled={enviando}
                    style={{ width: 18, height: 18, accentColor: "#d97706" }}
                  />
                  Setup (preparação de máquina — não avança a etapa)
                </label>

                {conclusaoRun && (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 12,
                      marginBottom: 12,
                    }}
                  >
                    <div>
                      <FieldLabel>Qtd boa</FieldLabel>
                      <Input
                        type="number"
                        min={0}
                        inputMode="numeric"
                        value={qtdBoa}
                        onChange={(e) => setQtdBoa(e.target.value)}
                        disabled={enviando}
                      />
                    </div>
                    <div>
                      <FieldLabel>Qtd refugo</FieldLabel>
                      <Input
                        type="number"
                        min={0}
                        inputMode="numeric"
                        value={qtdRefugo}
                        onChange={(e) => setQtdRefugo(e.target.value)}
                        disabled={enviando}
                      />
                    </div>
                  </div>
                )}

                {conclusaoRun && postoConferencia && (
                  <div style={{ marginBottom: 12 }}>
                    <FieldLabel>
                      Não-conformidades (Conferência — D12)
                    </FieldLabel>
                    {ncs.map((nc, idx) => (
                      <div
                        key={idx}
                        style={{
                          display: "grid",
                          gridTemplateColumns: "180px 1fr 56px",
                          gap: 8,
                          marginBottom: 8,
                        }}
                      >
                        <select
                          value={nc.tipo}
                          onChange={(e) => alterarNc(idx, "tipo", e.target.value)}
                          disabled={enviando}
                          style={{
                            padding: "12px",
                            fontSize: 16,
                            background: "#1a1d23",
                            color: "#fff",
                            border: "2px solid #3a4049",
                            borderRadius: 8,
                          }}
                        >
                          <option value="">Tipo…</option>
                          {TIPOS_NC.map((t) => (
                            <option key={t.codigo} value={t.codigo}>
                              {t.label}
                            </option>
                          ))}
                        </select>
                        <input
                          value={nc.descricao}
                          placeholder="Descrição (opcional)"
                          onChange={(e) =>
                            alterarNc(idx, "descricao", e.target.value)
                          }
                          disabled={enviando}
                          maxLength={300}
                          style={{
                            padding: "12px",
                            fontSize: 16,
                            background: "#1a1d23",
                            color: "#fff",
                            border: "2px solid #3a4049",
                            borderRadius: 8,
                          }}
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setNcs((prev) => prev.filter((_, i) => i !== idx))
                          }
                          disabled={enviando}
                          aria-label="Remover não-conformidade"
                          style={{
                            background: "#2a2f37",
                            color: "#fca5a5",
                            border: "none",
                            borderRadius: 8,
                            fontSize: 20,
                            cursor: "pointer",
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                    <SecondaryButton
                      type="button"
                      onClick={() =>
                        setNcs((prev) => [...prev, { tipo: "", descricao: "" }])
                      }
                      disabled={enviando}
                      style={{ minHeight: 44, padding: "10px 16px", fontSize: 15 }}
                    >
                      + Adicionar não-conformidade
                    </SecondaryButton>
                  </div>
                )}

                {acaoSugerida ? (
                  <ButtonRow>
                    {acaoSugerida === "CONCLUIR" ? (
                      <SuccessButton
                        onClick={() => executarBipagem("CONCLUIR")}
                        disabled={enviando}
                      >
                        {enviando ? "..." : `✓ ${ACAO_LABELS.CONCLUIR}${ehSetup ? " SETUP" : ""}`}
                      </SuccessButton>
                    ) : (
                      <PrimaryButton
                        onClick={() => executarBipagem(acaoSugerida)}
                        disabled={enviando}
                      >
                        {enviando
                          ? "..."
                          : `▶ ${ACAO_LABELS[acaoSugerida] || acaoSugerida}${ehSetup && acaoSugerida === "INICIAR" ? " SETUP" : ""}`}
                      </PrimaryButton>
                    )}
                    <SecondaryButton
                      onClick={limparParaProxima}
                      disabled={enviando}
                    >
                      Cancelar
                    </SecondaryButton>
                  </ButtonRow>
                ) : (
                  <Alert $tone="info" style={{ marginBottom: 0 }}>
                    Etapa já concluída neste posto para esta peça.
                  </Alert>
                )}

                {/* PAUSAR / RETOMAR — secundários (D2: pausas registradas). */}
                <Spacer $h={12} />
                <ButtonRow>
                  <SecondaryButton
                    onClick={() => executarBipagem("PAUSAR")}
                    disabled={enviando}
                    style={{ minHeight: 48, fontSize: 16 }}
                  >
                    ⏸ {ACAO_LABELS.PAUSAR}
                  </SecondaryButton>
                  <SecondaryButton
                    onClick={() => executarBipagem("RETOMAR")}
                    disabled={enviando}
                    style={{ minHeight: 48, fontSize: 16 }}
                  >
                    ⏵ {ACAO_LABELS.RETOMAR}
                  </SecondaryButton>
                </ButtonRow>
              </>
            )}
          </OpContextoBox>
        )}

        {!contexto && !carregandoContexto && (
          <p style={{ color: "#4a5260", fontSize: 14, textAlign: "center" }}>
            Digite o número da peça e pressione Enter (ou use o scanner).
          </p>
        )}

        {historico.length > 0 && (
          <>
            <Spacer $h={24} />
            <HistoricoRecente>
              <h3>Bipagens recentes desta sessão</h3>
              <ul>
                {historico.map((h) => (
                  <li key={h.id}>
                    <span>
                      <Pill
                        $tone={
                          h.acao === "CONCLUIR"
                            ? "success"
                            : h.acao === "PAUSAR"
                              ? "warning"
                              : "info"
                        }
                        style={{ marginRight: 8 }}
                      >
                        {ACAO_LABELS[h.acao] || h.acao}
                        {h.eh_setup ? " · SETUP" : ""}
                      </Pill>
                      {h.peca_numero}
                    </span>
                    <span className="right">
                      {formatarHora(h.timestamp)} · {h.situacao}
                      {h.override ? " · override" : ""}
                    </span>
                  </li>
                ))}
              </ul>
            </HistoricoRecente>
          </>
        )}

        <Spacer $h={24} />
        <DangerButton onClick={handleLogout}>Sair (Encerrar sessao)</DangerButton>
      </KioskCard>
    </KioskBody>
  );
}
